import styled, { createGlobalStyle } from 'styled-components';

export const GoldenPinDialogGlobalStyle = createGlobalStyle`
  /* Arabic: Droid Arabic Kufi is the primary font on every golden pin surface. */
  html[dir='rtl'] .golden-pin-modal-root,
  html[dir='rtl'] .golden-pin-modal-root *,
  html[dir='rtl'] .golden-pin-sheet-root,
  html[dir='rtl'] .golden-pin-sheet-root *,
  html[dir='rtl'] .golden-pin-apply,
  html[dir='rtl'] .golden-pin-apply *,
  html[dir='rtl'] .golden-pin-tooltip,
  html[dir='rtl'] .golden-pin-tooltip * {
    font-family: 'Droid Arabic Kufi', Figtree, sans-serif;
  }

  /* Short desktop viewports (e.g. a laptop at 100% scale): a narrower modal scales the map down so
     the whole modal fits like it does at 90%, and the body scrolls instead of touching the edges. */
  @media (max-height: 720px) {
    .golden-pin-apply.ant-modal {
      width: 600px !important;
      max-width: calc(100vw - 32px);
    }
  }
  @media (max-height: 640px) {
    .golden-pin-apply.ant-modal {
      width: 520px !important;
    }
  }

  /* The body can still scroll on very short screens, but never shows a scrollbar. */
  .golden-pin-apply .ant-modal-body {
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  .golden-pin-modal-root {
    .ant-modal-content {
      padding: 0;
      overflow: hidden;
      border-radius: 16px;
      box-shadow: 0 24px 60px rgba(23, 26, 31, 0.28);
    }
    .ant-modal-mask {
      background: rgba(23, 26, 31, 0.48);
    }
  }
  .golden-pin-sheet-root {
    .ant-drawer-mask {
      background: rgba(23, 26, 31, 0.42);
    }
    .ant-drawer-content {
      border-radius: 16px 16px 0 0;
      box-shadow: 0 -8px 40px rgba(23, 26, 31, 0.18);
    }
    .ant-drawer-body {
      padding: 0;
    }
  }
`;

export const SheetHandle = styled.div`
  width: 40px;
  height: 4px;
  margin: 8px auto 12px;
  border-radius: 2px;
  background: #d6d6d6;
  position: absolute;
  inset-block-start: 0;
  inset-inline: 0;
  z-index: 2;
`;

export const HeroWrap = styled.div`
  position: relative;
  line-height: 0;
  ${({ $mobile }) => $mobile && 'padding: 22px 16px 0; svg { border-radius: 12px; }'}
`;

export const CloseButton = styled.button`
  position: absolute;
  inset-block-start: ${({ $mobile }) => ($mobile ? '34px' : '12px')};
  inset-inline-end: ${({ $mobile }) => ($mobile ? '28px' : '12px')};
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 8px rgba(23, 26, 31, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #fff;
  }
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $mobile }) => ($mobile ? '12px' : '14px')};
  padding: ${({ $mobile }) => ($mobile ? '16px 16px 4px' : '20px 24px 4px')};
  max-height: 60vh;
  overflow-y: auto;
`;

export const Title = styled.div`
  color: #171a1f;
  font-size: ${({ $mobile }) => ($mobile ? '18px' : '21px')};
  font-weight: 700;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  gap: ${({ $mobile }) => ($mobile ? '9px' : '10px')};
`;

export const NewBadge = styled.span`
  flex: none;
  padding: ${({ $mobile }) => ($mobile ? '3px 8px' : '3px 9px')};
  line-height: 1.2;
  border-radius: 4px;
  background: #ce2929;
  color: #fff;
  font-size: ${({ $mobile }) => ($mobile ? '10px' : '11px')};
  font-weight: 700;
  letter-spacing: 0;

  /* Arabic is drawn by Droid Arabic Kufi, whose ink sits low in the line box: move 1.5px of the
     vertical padding from top to bottom (same total height) so the text reads centred. */
  html[dir='rtl'] & {
    padding-block: 1.5px 4.5px;
  }
`;

export const Description = styled.div`
  color: #626262;
  font-size: ${({ $mobile }) => ($mobile ? '13px' : '14px')};
  line-height: ${({ $mobile }) => ($mobile ? '19px' : '21px')};

  strong {
    color: #171a1f;
    font-weight: 700;
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: ${({ $mobile }) => ($mobile ? '12px 16px 16px' : '16px 24px 18px')};
`;

const baseButton = `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: background 160ms ease;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const PrimaryButton = styled.button`
  ${baseButton}
  height: ${({ $mobile, $large }) => ($mobile || $large ? ($mobile ? '48px' : '44px') : '42px')};
  padding: 0 ${({ $mobile }) => ($mobile ? '16px' : '22px')};
  ${({ $mobile, $block }) => ($block ? 'flex: none; width: 100%;' : $mobile ? 'flex: 1 1 0;' : '')}
  border: 0;
  border-radius: ${({ $mobile }) => ($mobile ? '8px' : '10px')};
  background: #006169;
  color: #fff;
  font-size: ${({ $large }) => ($large ? '14.5px' : '14px')};
  font-weight: 600;

  &:hover:not(:disabled) {
    background: #00494f;
  }
`;

export const FeatureStrip = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 16px 12px;
  border-radius: 12px;
  background: #fafafa;

  ${({ $mobile }) =>
    $mobile &&
    `
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 0;
    background: transparent;
  `}
`;

export const FeatureItem = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 7px;

  .gp-feature-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(23, 26, 31, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .gp-feature-title {
    color: #171a1f;
    font-size: 13px;
    font-weight: 700;
    line-height: 17px;
  }
  .gp-feature-text {
    color: #707070;
    font-size: 13px;
    line-height: 18px;
  }

  ${({ $mobile }) =>
    $mobile &&
    `
    flex-direction: row;
    align-items: center;
    text-align: start;
    gap: 12px;
    min-height: 58px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #fafafa;
    .gp-feature-icon { width: 30px; height: 30px; flex: none; }
    .gp-feature-title { font-size: 13px; }
    .gp-feature-text { font-size: 12px; line-height: 16px; }
  `}
`;

export const FeatureDivider = styled.div`
  flex: none;
  width: 1px;
  align-self: stretch;
  background: #ececec;
`;

export const HeroFrame = styled.div`
  overflow: hidden;
  border-radius: 8px;
  line-height: 0;
`;
